import { Circle } from '@mui/icons-material';
import GridViewIcon from '@mui/icons-material/GridView';
import {
    Box,
    FormControl,
    InputLabel,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Alert from '/@/components/bayer/alert';
import ModalV2 from '/@/components/bayer/modal-v2';
import RangeSlider from '/@/components/forms/range-slider';
import useUnitProvider from '/@/core/context/unit';
import { useH2H } from '/@/hooks/h2h';
import { useZones } from '/@/hooks/zones';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import { getSessionBrands, getSessionCountry, getUser } from '/@/store/session/selectors';
import type { Brand } from '/@/types/catalog';
import { range } from '/@/utils';
import EmptyState from '/@/views/dashboard/grower/components/empty-state';
import LinearRegression from '/@/views/head-to-head/components/modules/linear-regression';

const Watermark = ({ user, lines }: { user: any; lines: number }) => {
    return (
        <Box
            className='mp-mask'
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                opacity: 0.5,
                color: 'lightgray',
                overflow: 'hidden',
                zIndex: -1,

                '& > *': {
                    flex: '1 0 0',
                    display: 'flex',
                    alignItems: 'center',
                },
            }}
        >
            {range(lines * 2).map((line) => (
                <Box key={line}>{range(10).map(() => `${user.email} `)}</Box>
            ))}
        </Box>
    );
};

export const useH2HWrapper = ({
    product,
    enabled = true,
}: {
    product: { productId: number; brand?: Brand };
    enabled?: boolean;
}) => {
    const country = useSelector(getSessionCountry);
    const sessionBrands = useSelector(getSessionBrands);
    // The H2H anchor must be the viewed product's own brand, not an arbitrary first brand -
    // otherwise a Preceon product page would anchor comparisons on Dekalb's hybrids instead.
    const brand = product?.brand || sessionBrands['corn'][0];
    const allZonesQuery = useZones({
        type: 'corn',
    });
    const uniqueZones = useMemo(
        () =>
            (allZonesQuery.data || [])
                .filter((z) => z.country === country?.code)
                .filter((z, index, self) => index === self.findIndex((t) => _(t.shortName) === _(z.shortName))),
        [allZonesQuery.data, country]
    );

    const h2h = useH2H(
        {
            crop: 'corn',
            brand: brand,
            zoneIds: uniqueZones?.map((z) => z.id) || [],
            altitude: undefined,
            productId: product?.productId,
        },
        { enabled: enabled && !!product }
    );

    return {
        ...h2h,
        brand,
    };
};

const LinearRegressionModal = ({
    yieldTrials,
    environments,
    otherForLinearRegression,
    isValidating,
    others,
    crop,
    user,

    isOpen,
    setIsOpen,
}) => {
    const isInValid =
        !yieldTrials || !environments || !otherForLinearRegression || isValidating.results || !others.length;

    return (
        <ModalV2
            open={isOpen}
            onClose={() => setIsOpen(false)}
            title={_('Comportamiento del híbrido en todos los ambientes')}
            bodyProps={{
                width: '100%',
            }}
            maxWidth={'lg'}
        >
            {!isInValid && (
                <LinearRegression
                    displayHeader={false}
                    crop={crop}
                    user={user}
                    headToHead={yieldTrials}
                    other={otherForLinearRegression}
                    otherOptions={others}
                    type={environments.type}
                />
            )}
        </ModalV2>
    );
};

export const H2HMiniCard = ({ product, onClick }: { product: any; onClick: () => void }) => {
    const { head, filteredYieldTable: yieldTable, zone, isValidating, brand } = useH2HWrapper({ product });
    const units = useUnitProvider();

    const filteredYieldTable = useMemo(() => {
        if (!yieldTable) return [];
        const sorted = [...yieldTable]
            .toSorted((a, b) => b.value - a.value)
            .filter((row) => row.other.brand.id === brand.id);
        const winners = sorted.filter((row) => row.result === 'win').slice(0, 2);
        if (winners.length === 2) return winners;
        return [...winners, ...sorted.filter((row) => !winners.includes(row))].slice(0, 2);
    }, [yieldTable, brand]);

    if (!head) return null;

    return (
        <Stack spacing={2} p={2} sx={{ border: (t) => `1px solid ${t.palette.divider}` }}>
            <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                {_('Principales competidores del mercado')}
            </Typography>
            <Stack spacing={1}>
                <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='caption' color='primary.mui.disabled'>
                        {_('Hibrido')}
                    </Typography>
                    <Typography variant='caption' color='primary.mui.disabled'>
                        {`${_('Diferencia')} ${product.catalogName}`}
                    </Typography>
                </Stack>
                {isValidating.results &&
                    [...Array(2)].map((_, i) => (
                        <Stack key={i} direction='row' justifyContent='space-between'>
                            <Typography variant='subtitle2' fontWeight='bold' color='primary'>
                                <Skeleton variant='text' width={100} />
                            </Typography>
                            <Typography variant='subtitle2' fontWeight='bold'>
                                <Skeleton variant='text' width={100} />
                            </Typography>
                        </Stack>
                    ))}
                {filteredYieldTable &&
                    filteredYieldTable.length > 0 &&
                    filteredYieldTable.map((row) => (
                        <Stack key={row.id} direction='row' justifyContent='space-between'>
                            <Typography variant='subtitle2' fontWeight='bold' color='primary'>
                                {row.other.name} ({_(zone?.shortName)})
                            </Typography>
                            <Typography
                                variant='subtitle2'
                                fontWeight='bold'
                                color={
                                    row.result === 'win' ? 'success.main' : row.result === 'lose' ? 'error' : 'primary'
                                }
                            >
                                {`${row.value > 0 ? '+' : '-'} ${units.toYield(Math.abs(row.value), 'corn', 2).toString()} `}
                            </Typography>
                        </Stack>
                    ))}
            </Stack>
            <Typography
                variant='body1'
                color='secondary'
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                onClick={onClick}
            >
                {_('Ver todas las comparaciones')}
            </Typography>
        </Stack>
    );
};

const ProductExplorationH2H = ({ product }: { product: any }) => {
    const {
        head,

        zone,
        setZone,
        zoneOptions,

        environment,
        environments,
        environmentOptions,
        setEnvironment,
        selectedEnvironmentOptionValue,

        yieldBrands,
        setYieldBrands,
        yieldBrandOptions,

        yieldTrials,
        filteredYieldTable,

        otherForLinearRegression,
        isValidating,
        others,
        setOtherForLinearRegression,
    } = useH2HWrapper({ product });

    const units = useUnitProvider();
    const user = useSelector(getUser);

    const [linearRegressionModalIsOpen, setLinearRegressionModalIsOpen] = useState(false);

    if (!head) return null;
    return (
        <Stack spacing={4}>
            <Alert severity='danger'>{_('Esta información es confidencial. Por favor, no la compartas.')}</Alert>
            <Stack spacing={2}>
                <Typography variant='h5' color='primary'>
                    {_('Diferencia de rendimiento entre {{product}} y otros híbridos del mercado', {
                        product: product.catalogName,
                    })}
                </Typography>
                <Typography variant='body1' color='primary.mui.inactive'>
                    {_(
                        'Mirá cómo rinde este híbrido en ensayos frente a otras opciones del mercado. El resultado indica si supera o no a sus competidores.'
                    )}
                </Typography>
            </Stack>
            <Stack spacing={3}>
                <Grid2 container spacing={1}>
                    <Grid2 xs={12} md={4}>
                        <MuiSelect
                            options={yieldBrandOptions}
                            value={yieldBrands}
                            onChange={(e) => setYieldBrands(e.target.value)}
                            inputLabel={_('Marcas')}
                            formControlProps={{
                                fullWidth: true,
                            }}
                            multiple
                        />
                    </Grid2>
                    {environments?.type === 'options' &&
                        environments.options.length > 0 &&
                        selectedEnvironmentOptionValue && (
                            <Grid2 xs={12} md={4}>
                                <MuiSelect
                                    inputLabel={_('Seleccioná el ambiente')}
                                    clearable={false}
                                    value={selectedEnvironmentOptionValue}
                                    options={environmentOptions}
                                    onChange={(e) => setEnvironment(e.target.value)}
                                    formControlProps={{
                                        fullWidth: true,
                                    }}
                                />
                            </Grid2>
                        )}
                    {environments?.type === 'slider' && (
                        <Grid2 xs={12} md={4}>
                            <FormControl fullWidth sx={{ px: 2 }}>
                                <InputLabel label={_('Ambiente')} />
                                <RangeSlider
                                    step={100}
                                    min={Math.round(environments.minEnvironment / 100) * 100}
                                    max={Math.round(environments.maxEnvironment / 100) * 100}
                                    innerMin={Math.round(environments.maxEnvironment / 100) * 100}
                                    innerMax={Math.round(environments.minEnvironment / 100) * 100}
                                    value={environment ? [environment.min, environment.max] : [0, 0]}
                                    unit={units.getUnit('yield', 'corn')}
                                    format={(value) => units.toYield(value, 'corn', 2).value}
                                    onChange={(value) => setEnvironment({ min: value[0], max: value[1] })}
                                />
                            </FormControl>
                        </Grid2>
                    )}
                    {zone && zoneOptions.length > 1 && (
                        <Grid2 xs={12} md={4}>
                            <MuiSelect
                                options={zoneOptions}
                                value={zone}
                                onChange={(e) => setZone(e.target.value)}
                                inputLabel={_('Siembra')}
                                formControlProps={{
                                    fullWidth: true,
                                }}
                            />
                        </Grid2>
                    )}
                </Grid2>
                <Table
                    sx={{
                        '& .MuiTableRow-root:hover': { backgroundColor: 'unset' },
                        overflowX: 'scroll',
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell width={'33%'}>
                                <Typography variant='subtitle1' fontWeight='bold' color='primary.mui.inactive'>
                                    {_('Híbrido')}
                                </Typography>
                            </TableCell>
                            <TableCell width={'33%'}>
                                <Typography variant='subtitle1' fontWeight='bold' color='primary.mui.inactive'>
                                    {_('Diferencia de {{product}}', { product: product.catalogName })}
                                </Typography>
                            </TableCell>
                            <TableCell width={'33%'} />
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ position: 'relative' }}>
                        <Watermark user={user} lines={filteredYieldTable?.length || 0} />
                        {isValidating.results &&
                            [...Array(5)].map((_, i) => (
                                <TableRow key={i}>
                                    <TableCell>
                                        <Skeleton variant='text' width={100} height={40} />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton variant='text' width={100} height={40} />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton variant='text' width={100} height={20} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        {(filteredYieldTable || []).map((row) => (
                            <TableRow key={row.id}>
                                <TableCell>
                                    <Stack spacing={1}>
                                        <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                                            {row.other.name}
                                        </Typography>
                                        <Typography variant='body2' color='primary.mui.inactive'>
                                            {row.other.brand.name}
                                        </Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Stack spacing={1}>
                                        <Typography
                                            variant='subtitle1'
                                            fontWeight='bold'
                                            color={
                                                row.result === 'win'
                                                    ? 'success.main'
                                                    : row.result === 'lose'
                                                      ? 'error'
                                                      : 'primary'
                                            }
                                        >
                                            {`${row.value > 0 ? '+' : '-'} ${units.toYield(Math.abs(row.value), 'corn', 2).toString()} `}
                                        </Typography>
                                        <Typography variant='body2' color='primary.mui.inactive'>
                                            {`${row.trialCount} ${_('Ensayos')} - ${row.winRate.toFixed(2)}% ${_('win rate')}`}
                                        </Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    {row.linearRegressionEnabled && (
                                        <Typography
                                            color={'primary'}
                                            sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                                            variant='body2'
                                            onClick={() => {
                                                setLinearRegressionModalIsOpen(true);
                                                setOtherForLinearRegression(
                                                    others.find(({ id }) => id === row.other.id)
                                                );
                                            }}
                                        >
                                            {_('Regresión lineal')}
                                        </Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <LinearRegressionModal
                    yieldTrials={yieldTrials}
                    environments={environments}
                    otherForLinearRegression={otherForLinearRegression}
                    isValidating={isValidating}
                    others={others}
                    crop={'corn'}
                    user={user}
                    isOpen={linearRegressionModalIsOpen}
                    setIsOpen={setLinearRegressionModalIsOpen}
                />
                <Stack
                    direction='row'
                    spacing={4}
                    p={2}
                    sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 1, overflowX: 'scroll' }}
                    width={{ xs: 'auto', sm: 'fit-content' }}
                >
                    <Stack direction='row' spacing={1} alignItems={'center'}>
                        <Circle color='success' />
                        <Typography variant='body2' color='primary'>
                            {_('Gana')}
                        </Typography>
                    </Stack>
                    <Stack direction='row' spacing={1} alignItems={'center'}>
                        <Circle color='primary' />
                        <Typography variant='body2' color='primary'>
                            {_('Diferencia no significativa')}
                        </Typography>
                    </Stack>
                    <Stack direction='row' spacing={1} alignItems={'center'}>
                        <Circle color='error' />
                        <Typography variant='body2' color='primary'>
                            {_('Pierde')}
                        </Typography>
                    </Stack>
                </Stack>
                {filteredYieldTable?.length === 0 && (
                    <Stack sx={{ py: 5 }}>
                        <EmptyState
                            title={_('No se encontraron resultados')}
                            subtitle={_('No hemos encontrado híbridos que coincidan con estos criterios')}
                            icon={<GridViewIcon sx={{ width: 24, height: 24 }} />}
                        />
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};

export default ProductExplorationH2H;
